import { useState } from 'react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { formatCurrency, formatOdds, calculatePayout, calculateWinnings, calculateROI } from '../../utils';
import type { Bet, Fight } from '../../types';

interface MyBetsListProps {
  bets: Bet[];
  fights: Fight[];
}

/**
 * My Bets List Component
 * Displays all bets placed by the current participant with enhanced details
 */
export function MyBetsList({ bets, fights }: MyBetsListProps) {
  const [sortBy, setSortBy] = useState<'date' | 'amount' | 'status'>('date');
  const [filterBy, setFilterBy] = useState<'all' | 'pending' | 'won' | 'lost'>('all');

  if (bets.length === 0) {
    return (
      <Card padding="md">
        <div className="text-center py-8">
          <p className="text-secondary-text">No bets placed yet</p>
          <p className="text-muted-text text-sm mt-2">
            Place your first bet to get started!
          </p>
        </div>
      </Card>
    );
  }

  const getFightName = (fightId: string): string => {
    const fight = fights.find((f) => f.id === fightId);
    if (!fight) return 'Unknown Fight';
    return fight.fighters.map((f) => f.name).join(' vs ');
  };

  const getFighterName = (fightId: string, fighterId: string): string => {
    const fight = fights.find((f) => f.id === fightId);
    if (!fight) return 'Unknown Fighter';
    const fighter = fight.fighters.find((f) => f.id === fighterId);
    return fighter?.name || 'Unknown Fighter';
  };

  const getFightStatus = (fightId: string): Fight['status'] | null => {
    const fight = fights.find((f) => f.id === fightId);
    return fight?.status || null;
  };

  const getFightResult = (fightId: string): string | null => {
    const fight = fights.find((f) => f.id === fightId);
    if (!fight || !fight.result) return null;
    
    const result = fight.result;
    const winner = fight.fighters.find(f => f.id === result.winnerId);
    const winnerName = winner?.name || 'Unknown';
    
    // Format method
    let methodStr = '';
    switch (result.method) {
      case 'ko_tko':
        methodStr = 'KO/TKO';
        break;
      case 'submission':
        methodStr = 'Submission';
        break;
      case 'decision_unanimous':
        methodStr = 'Unanimous Decision';
        break;
      case 'decision_split':
        methodStr = 'Split Decision';
        break;
      case 'decision_majority':
        methodStr = 'Majority Decision';
        break;
      case 'dq':
        methodStr = 'DQ';
        break;
      case 'draw':
        return 'Draw';
      case 'no_contest':
        return 'No Contest';
      default:
        methodStr = result.method;
    }
    
    // Format round/time if applicable
    if (result.wentTheDistance) {
      return `${winnerName} by ${methodStr}`;
    } else if (result.round && result.timeInRound) {
      return `${winnerName} by ${methodStr} (R${result.round}, ${result.timeInRound})`;
    } else if (result.round) {
      return `${winnerName} by ${methodStr} (R${result.round})`;
    } else {
      return `${winnerName} by ${methodStr}`;
    }
  };

  const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Filter bets
  const filteredBets = filterBy === 'all' 
    ? bets 
    : bets.filter((bet) => bet.status === filterBy);

  // Sort bets
  const sortedBets = [...filteredBets].sort((a, b) => {
    if (sortBy === 'date') {
      return b.placedAt - a.placedAt; // Most recent first
    } else if (sortBy === 'amount') {
      return b.amount - a.amount; // Highest first
    } else {
      // Sort by status: pending first, then won, then lost
      const statusOrder = { pending: 0, won: 1, lost: 2 };
      return statusOrder[a.status] - statusOrder[b.status];
    }
  });

  // Group bets by status for summary
  const pendingBets = bets.filter((b) => b.status === 'pending');
  const wonBets = bets.filter((b) => b.status === 'won');
  const totalWinnings = wonBets.reduce((sum, bet) => sum + (bet.payout || 0), 0);
  const totalProfit = wonBets.reduce((sum, bet) => sum + calculateWinnings(bet.amount, bet.odds), 0);

  const statusVariants = {
    pending: 'warning',
    won: 'success',
    lost: 'error',
  } as const;

  const statusLabels = {
    pending: 'Pending',
    won: 'Won',
    lost: 'Lost',
  };

  return (
    <Card padding="md">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-primary-text mb-4">My Bets</h2>
        
        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="bg-tertiary-bg rounded-lg p-3 border border-border">
            <div className="text-xs text-secondary-text mb-1">Total Bets</div>
            <div className="text-lg font-bold text-primary-text">{bets.length}</div>
          </div>
          <div className="bg-tertiary-bg rounded-lg p-3 border border-border">
            <div className="text-xs text-secondary-text mb-1">Pending</div>
            <div className="text-lg font-bold text-warning-yellow">{pendingBets.length}</div>
          </div>
          <div className="bg-tertiary-bg rounded-lg p-3 border border-border">
            <div className="text-xs text-secondary-text mb-1">Won</div>
            <div className="text-lg font-bold text-success-green">{wonBets.length}</div>
          </div>
        </div>

        {wonBets.length > 0 && (
          <div className="bg-success-green/10 border border-success-green rounded-lg p-3 mb-4">
            <div className="text-sm text-secondary-text mb-1">Total Winnings</div>
            <div className="text-xl font-bold text-success-green">{formatCurrency(totalWinnings)}</div>
            <div className="text-xs text-secondary-text mt-1">
              Profit: {formatCurrency(totalProfit)}
            </div>
          </div>
        )}

        {/* Filters and Sort */}
        <div className="flex gap-2 mb-4">
          <select
            value={filterBy}
            onChange={(e) => setFilterBy(e.target.value as typeof filterBy)}
            className="px-3 py-2 bg-tertiary-bg border border-border rounded-lg text-sm text-primary-text"
          >
            <option value="all">All Bets</option>
            <option value="pending">Pending</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
            className="px-3 py-2 bg-tertiary-bg border border-border rounded-lg text-sm text-primary-text"
          >
            <option value="date">Sort by Date</option>
            <option value="amount">Sort by Amount</option>
            <option value="status">Sort by Status</option>
          </select>
        </div>
      </div>

      <div className="space-y-3">
        {sortedBets.map((bet) => {
          const potentialPayout = calculatePayout(bet.amount, bet.odds);
          const potentialProfit = calculateWinnings(bet.amount, bet.odds);
          const potentialROI = calculateROI(bet.amount, bet.odds);
          const fightName = bet.isParlay ? 'Parlay Bet' : getFightName(bet.fightId);
          const fighterName = bet.isParlay 
            ? `${bet.parlayLegs?.length || 0}-Leg Parlay`
            : bet.fighterId
            ? getFighterName(bet.fightId, bet.fighterId)
            : (bet.outcomeName || 'Prop Bet');
          const fightStatus = bet.isParlay ? null : getFightStatus(bet.fightId);
          const actualProfit = bet.status === 'won' && bet.payout 
            ? calculateWinnings(bet.amount, bet.odds)
            : null;
          const actualROI = actualProfit !== null 
            ? (actualProfit / bet.amount) * 100
            : null;

          return (
            <div
              key={bet.id}
              className={`p-4 bg-tertiary-bg rounded-lg border-2 ${
                bet.status === 'won' 
                  ? 'border-success-green bg-success-green/5' 
                  : bet.status === 'lost' 
                  ? 'border-error/30 bg-error/5' 
                  : 'border-border'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="font-semibold text-lg text-primary-text">{fighterName}</div>
                    <Badge variant={statusVariants[bet.status]} size="sm">
                      {statusLabels[bet.status]}
                    </Badge>
                    {fightStatus && (
                      <Badge variant={fightStatus === 'live' ? 'error' : fightStatus === 'completed' ? 'default' : 'info'} size="sm">
                        {fightStatus}
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm text-secondary-text">{fightName}</div>
                  <div className="text-xs text-muted-text mt-1">
                    Placed: {formatTimestamp(bet.placedAt)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-border">
                <div>
                  <div className="text-xs text-secondary-text mb-1">Bet Amount</div>
                  <div className="font-semibold text-primary-text">
                    {formatCurrency(bet.amount)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-secondary-text mb-1">Odds</div>
                  <div className="font-semibold text-primary-text">
                    {formatOdds(bet.odds)}
                  </div>
                </div>
              </div>

              {bet.status === 'pending' && (
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-secondary-text mb-1">Potential Payout</div>
                      <div className="font-semibold text-primary-text">
                        {formatCurrency(potentialPayout)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-secondary-text mb-1">Potential Profit</div>
                      <div className="font-semibold text-success-green">
                        {formatCurrency(potentialProfit)} ({potentialROI.toFixed(1)}%)
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {bet.status === 'won' && bet.payout && (
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-secondary-text mb-1">Payout</div>
                      <div className="font-semibold text-success-green text-lg">
                        {formatCurrency(bet.payout)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-secondary-text mb-1">Profit</div>
                      <div className="font-semibold text-success-green text-lg">
                        {formatCurrency(actualProfit || 0)} ({actualROI?.toFixed(1)}%)
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {bet.status === 'lost' && (
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="text-xs text-muted-text">Bet lost - {formatCurrency(bet.amount)} deducted</div>
                </div>
              )}

              {/* Show fight result for resolved bets */}
              {(bet.status === 'won' || bet.status === 'lost') && !bet.isParlay && (
                <div className="mt-3 pt-3 border-t border-border">
                  <div className="text-xs text-secondary-text">
                    <span className="font-medium">Result:</span>{' '}
                    <span className="text-primary-text">{getFightResult(bet.fightId) || 'Result pending'}</span>
                  </div>
                </div>
              )}

              {/* Parlay Legs */}
              {bet.isParlay && bet.parlayLegs && bet.parlayLegs.length > 0 && (
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="text-sm font-semibold text-primary-text mb-2">Parlay Legs:</div>
                  <div className="space-y-2">
                    {bet.parlayLegs.map((leg, index) => {
                      const legResult = (leg.status === 'won' || leg.status === 'lost') 
                        ? getFightResult(leg.fightId) 
                        : null;
                      
                      return (
                        <div 
                          key={index}
                          className={`p-2 rounded border ${
                            leg.status === 'won' 
                              ? 'bg-success-green/10 border-success-green' 
                              : leg.status === 'lost' 
                              ? 'bg-error/10 border-error' 
                              : 'bg-secondary-bg border-border'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="text-sm font-medium text-primary-text">{leg.fightName}</div>
                              <div className="text-xs text-secondary-text mt-1">
                                {leg.selection} ({leg.odds > 0 ? '+' : ''}{leg.odds})
                              </div>
                              {legResult && (
                                <div className="text-xs text-muted-text mt-1">
                                  Result: {legResult}
                                </div>
                              )}
                            </div>
                            <div className="ml-2">
                              {leg.status === 'won' && (
                                <Badge variant="success" size="sm">✓ Won</Badge>
                              )}
                              {leg.status === 'lost' && (
                                <Badge variant="error" size="sm">✗ Lost</Badge>
                              )}
                              {leg.status === 'pending' && (
                                <Badge variant="warning" size="sm">Pending</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {sortedBets.length === 0 && (
          <div className="text-center py-8">
            <p className="text-secondary-text">No bets match the selected filter</p>
          </div>
        )}
      </div>
    </Card>
  );
}


